import { DefaultButton, DetailsList, IColumn, Icon, Link, MessageBar, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { EntityAPI, EntityType, IContentHubType, IEntityColumn, IEntityToken, ITrigger, IUserConfigPackage, TriggerEvaluationPoint } from '../../../API';
import TriggerEditor, { TriggerEditorState } from './TriggerEditor';

export class TriggersState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: TriggerEditorState;
	
	public tokens?: IEntityToken[];
	public listUrl: string = '';
	
	update: (ids: string[]) => void;
	
	items: ITrigger[] = [];
	availableContentTypes: IContentHubType[] = [];

	constructor(userConfig: IUserConfigPackage,  update:(ids:string[]) => void, listUrl: string	= '') {
		this.userConfig = userConfig;
		this.update = update;
		this.listUrl = listUrl;

		this.editState = new TriggerEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this), listUrl);

		makeAutoObservable(this);
	}
	
	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
		this.editState.setListUrl(listUrl);
	}

	public setAvailableTokens(tokens: IEntityToken[]) {
		this.tokens = tokens;
		this.editState.setTokens(tokens);
	}
	
	public setAvailableFields(listUrlColumns: IEntityColumn[]) {
		this.editState.setAvailableFields(listUrlColumns);
	}
	
	public async setSelected(ids:string[]) {
		this.loading = true;

		if(ids.length > 0) {
			let api = await this.getApi();
			let profiles = await api.items(ids, null);
			this.setItems(profiles);
		} else {
			this.setItems([]);
		}
	}

	public setAvailableContentTypes(types: IContentHubType[]) {
		this.availableContentTypes = types;
	}

	private async getApi() : Promise<EntityAPI<ITrigger>> {
		return await EntityAPI.fromEntityType(
			EntityType.Trigger,
			this.userConfig,
		) as EntityAPI<ITrigger>;
	}

	setItems(items: ITrigger[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: ITrigger) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {
		this.edit({
			
			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
			TriggerExpression: '',
			TriggerActionIds: [],
			TriggerEvaluationPoint: TriggerEvaluationPoint.BeforeEval
		});
	}

	public async save(original:ITrigger, updated:ITrigger) : Promise<void> {
		
		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:ITrigger) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(profile:ITrigger) {
		this.items = this.items.filter(p => p !== profile);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		this.update(this.items.map(p => p.Id));
	}
}

function Triggers({ state }: { state: TriggersState }) {

	let columns: IColumn[] = [
		{
			key: 'expression',
			name: '',
			minWidth: 40,
			onRender: (item: ITrigger) => <span>
				<Link title={item.TriggerExpression} onClick={() => { state.edit(item) }}>{item.TriggerExpression}</Link>
			</span>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 60,
			maxWidth: 60,
			onRender: (item: ITrigger) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<>
			<>
				{state.editState && <TriggerEditor state={state.editState} /> }

				{state.items.length === 0 ?
					<MessageBar>No triggers have been configured. Click 'Add trigger' to add a new trigger.</MessageBar>
					:
					<></>
				}
				
				{(state?.items && state.items.length) ?
					<DetailsList isHeaderVisible={false} selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
			</>

			<div style={{textAlign: 'right', marginTop:20}}>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add trigger</DefaultButton>
			</div>
		</>
	);
}

export default observer(Triggers);
